<template>
  <div class="trip-map">
    <mapbox ref="map" @load="onLoad">
      <div ref="markerStart" class="marker"></div>
      <div ref="markerEnd" class="marker"></div>
      <div ref="markerCurrent" class="marker"></div>
    </mapbox>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import Mapbox from 'views/elements/map/Mapbox';
import MapboxWrapper from 'views/elements/map/MapboxWrapper';
import { mapGetters } from 'vuex';
import { get, has, map, reduce } from 'lodash';
import { select, remove } from 'd3-selection';

export default {
  name: 'TripMap',

  components: {
    Mapbox,
  },

  mixins: [MapboxWrapper],

  data() {
    return {
      markerStart: null,
      markerEnd: null,
      markerCurrent: null,
      isLoaded: false,
    };
  },

  computed: {
    ...mapGetters('trip', ['waypoints']),

    ...mapGetters('account', ['locale']),

    coordinates() {
      const coordinates = map(this.waypoints, waypoint => [
        waypoint.longitude,
        waypoint.latitude,
      ]);

      return coordinates;
    },

    splittedWaypoints() {
      let waypoints = this.waypoints;
      let previousAction;

      waypoints = reduce(
        waypoints,
        (accumulator, currentValue, currentIndex) => {
          const currentAction =
            currentValue.speed > currentValue.speed_limit
              ? 'speeding'
              : 'normal';

          if (currentAction === previousAction) {
            accumulator[accumulator.length - 1].coordinates = [
              ...accumulator[accumulator.length - 1].coordinates,
              [currentValue.longitude, currentValue.latitude],
            ];
          } else {
            let coordinates;
            const currentCoords = [
              currentValue.longitude,
              currentValue.latitude,
            ];

            if (currentIndex > 0) {
              const previousValue = waypoints[currentIndex - 1];
              const previousCoords = [
                previousValue.longitude,
                previousValue.latitude,
              ];

              coordinates = [previousCoords, currentCoords];
            } else {
              coordinates = [currentCoords];
            }

            accumulator = [
              ...accumulator,
              {
                action: currentAction,
                coordinates: coordinates,
              },
            ];
          }

          previousAction = currentAction;

          return accumulator;
        },
        []
      );

      return waypoints;
    },

    features() {
      return map(this.splittedWaypoints, item => {
        return {
          type: 'Feature',
          properties: {
            color: item.action === 'speeding' ? '#e57373' : '#5886e1',
          },
          geometry: {
            type: 'LineString',
            coordinates: item.coordinates,
          },
        };
      });
    },
  },

  watch: {
    locale() {
      this.removeLegend();
      this.addLegend();
    },
  },

  activated() {
    if (this.isLoaded) {
      this.exit();
      this.update();

      setTimeout(() => {
        this.map.resize();
      }, 10);
    }
  },

  deactivated() {},

  methods: {
    onLoad() {
      this.update();
      this.addLegend();
      this.isLoaded = true;

      setTimeout(() => {
        this.map.resize();
      }, 10);
    },

    update() {
      this.setBounds();
      this.addRoute();
      this.addMarkers();
      // this.addMouseListeners()
    },

    exit() {
      this.map.removeLayer('route');
      this.map.removeSource('route');

      if (this.markerStart) {
        this.markerStart.remove();
      }

      if (this.markerEnd) {
        this.markerEnd.remove();
      }

      if (this.markerCurrent) {
        this.markerCurrent.remove();
      }
    },

    addRoute() {
      this.map.addLayer({
        id: 'route',
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: this.features,
          },
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2,
        },
      });
    },

    addMarkers() {
      if (this.coordinates.length === 0) {
        return;
      }

      this.markerStart = new mapboxgl.Marker(this.$refs.markerStart)
        .setLngLat(this.coordinates[0])
        .addTo(this.map);

      this.markerEnd = new mapboxgl.Marker(this.$refs.markerEnd)
        .setLngLat(this.coordinates[this.coordinates.length - 1])
        .addTo(this.map);

      this.markerCurrent = new mapboxgl.Marker(this.$refs.markerCurrent)
        .setLngLat(this.coordinates[0])
        .addTo(this.map);
    },

    addMouseListeners() {
      this.map.on('mousemove', 'route', e => {
        this.markerCurrent.setLngLat(e.lngLat);
      });

      this.map.on('mouseenter', 'route', e => {
        const current = this.markerCurrent.getElement();
        const start = this.markerStart.getElement();
        const end = this.markerEnd.getElement();

        select(start).classed('is-hidden', true);
        select(end).classed('is-hidden', true);
        select(current).classed('is-hidden', false);
      });

      this.map.on('mouseout', 'route', e => {
        const current = this.markerCurrent.getElement();
        const start = this.markerStart.getElement();
        const end = this.markerEnd.getElement();

        select(start).classed('is-hidden', false);
        select(end).classed('is-hidden', false);
        select(current).classed('is-hidden', true);
      });
    },

    setBounds() {
      if (this.coordinates.length === 0) {
        return;
      }

      const bounds = reduce(
        this.coordinates,
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(this.coordinates[0], this.coordinates[0])
      );

      this.map.fitBounds(bounds, { padding: 40 });
    },

    addLegend() {
      select('.trip-map .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl')
        .append('div')
        .attr('class', 'legend').html(`
          <div class="legend-item">
            <span class="key-line" style="background-color: #5886e1"></span>
            ${this.$t('trip.map.normal')}
          </div>
          <div class="legend-item">
            <span class="key-line" style="background-color: #e57373"></span>
            ${this.$t('trip.map.speeding')}
          </div>
        `);
    },

    removeLegend() {
      select(
        '.trip-map .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl .legend'
      ).remove();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.trip-map {
  height: 100%;
  box-shadow: 0 2px 4px 0 rgba(58, 153, 252, 0.08),
    0 1px 2px 0 rgba($black, 0.08);
}

.marker {
  &:not(.mapboxgl-marker) {
    display: none;
  }

  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid $blue;
  background-color: $white;

  &::before {
    content: ' ';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: $blue;
  }
}
</style>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.trip-map {
  .mapboxgl-ctrl-bottom-left {
    .mapboxgl-ctrl {
      margin-left: 1rem;
    }
  }

  &:hover {
    .legend {
      opacity: 0.24;
    }
  }

  .legend {
    display: flex;
    padding: 0.25rem 0.5rem;
    background-color: #eff4f9;
    border-radius: 3px;
    transition: $transition;

    .legend-item {
      display: flex;
      align-items: center;
      line-height: 1.33;
      color: rgba($black, 0.72);

      &:not(:last-child) {
        margin-right: 0.75rem;
      }

      span {
        display: flex;
        width: 1rem;
        height: 0.125rem;
        border-radius: 2px;
        margin-right: 0.5rem;
      }
    }
  }
}
</style>

